/* ==========================================================================
   Kopstorm CRO Suite - Wave 8 behaviour
   W74/W74b buy area reassurance · W75 gallery slide promotion ·
   W76 cart drawer threshold · W77 card spec chip · W78 market strip ·
   W79 homepage focus

   W74, W74b, W77, W78 and W79 need no JavaScript at all: every arm is
   rendered server side and CSS decides what is visible, so an Intelligems arm
   change is a single frame and a failed script cannot strand a shopper.

   This file owns only the three behaviours that CSS cannot express:
     - moving a gallery slide (W75)
     - the running threshold maths and the UpCart clone (W76)
     - the shared info sheet that W74 and W77 open

   Control purity rule for this store: never hide a theme element by writing
   `hidden` in JavaScript. An author display rule beats the user agent
   [hidden] rule, so anything printed into stock markup is hidden in CSS.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  var W8 = window.KopstormW8 || {};
  var copy = W8.copy || {};
  var conf = W8.settings || {};

  function t(key, fallback) {
    var value = copy[key];
    return typeof value === 'string' && value !== '' ? value : fallback;
  }

  function has(flag) {
    return root.classList.contains('kop-' + flag);
  }

  /* Editor preview and the preview build reveal everything, exactly like the
     earlier waves, so a reviewer never has to know the flag ids. */
  function on(flag) {
    return has(flag) || has('w8preview') || has('w8ed-' + flag);
  }

  function onGallery() {
    return on('w75') || has('w8ed-gallery');
  }

  function onCart() {
    return on('w76') || has('w8ed-cart');
  }

  function money(cents) {
    var value = (Math.round(cents) / 100).toFixed(2);
    var format = conf.money_format || '${{amount}}';
    /* Shopify money formats use curly placeholders. Only the plain amount
       forms are handled here; anything else falls back to the raw number with
       the format's own currency text preserved. */
    return format
      .replace(/\{\{\s*amount\s*\}\}/, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, String(Math.round(cents / 100)));
  }

  /* ------------------------------------------------------------------------
     Shared info sheet
     Prefers Wave 4's KopstormPop host when it is on the page, so the store
     never shows two different popover treatments, and falls back to its own
     bottom sheet otherwise. A module must not depend on another wave's master
     switch being enabled.
     ------------------------------------------------------------------------ */

  var lockCount = 0;
  var lockOffset = 0;

  function lockScroll() {
    lockCount += 1;
    if (lockCount > 1) return;
    lockOffset = window.scrollY || window.pageYOffset || 0;
    /* Pinning the body removes the scrollbar and shifts the page sideways, so
       the gutter is held open. iOS Safari scrolls the document even with
       overflow hidden, which is why this is position fixed. */
    var gutter = window.innerWidth - doc.documentElement.clientWidth;
    doc.body.style.position = 'fixed';
    doc.body.style.top = -lockOffset + 'px';
    doc.body.style.insetInline = '0';
    if (gutter > 0) doc.body.style.paddingRight = gutter + 'px';
    root.classList.add('kop8-locked');
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount > 0) return;
    doc.body.style.position = '';
    doc.body.style.top = '';
    doc.body.style.insetInline = '';
    doc.body.style.paddingRight = '';
    root.classList.remove('kop8-locked');
    window.scrollTo({ top: lockOffset, behavior: 'instant' });
  }

  var sheet = null;
  var lastTrigger = null;

  function findSheet() {
    if (!sheet || !sheet.isConnected) sheet = doc.querySelector('[data-kop8-sheet]');
    return sheet;
  }

  function openSheet(title, points, trigger) {
    var node = findSheet();
    if (!node) return;

    lastTrigger = trigger || null;

    var titleNode = node.querySelector('[data-kop8-sheet-title]');
    var listNode = node.querySelector('[data-kop8-sheet-points]');
    if (titleNode) titleNode.textContent = title || '';

    if (listNode) {
      /* Built with the DOM rather than innerHTML: these strings come from
         merchant settings and product metafields, which must never be able to
         inject markup. */
      listNode.textContent = '';
      points.forEach(function (text) {
        if (!text) return;
        var li = doc.createElement('li');
        li.className = 'kop8-sheet__point text-sm leading-tight';
        var icon = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
        icon.setAttribute('viewBox', '0 0 20 20');
        icon.setAttribute('aria-hidden', 'true');
        icon.setAttribute('fill', 'none');
        var path = doc.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M4 10.5l4 4 8-9');
        path.setAttribute('stroke', 'currentColor');
        path.setAttribute('stroke-width', '2');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        icon.appendChild(path);
        var span = doc.createElement('span');
        span.textContent = text;
        li.appendChild(icon);
        li.appendChild(span);
        listNode.appendChild(li);
      });
    }

    node.setAttribute('data-kop8-open', '');
    lockScroll();
    var close = node.querySelector('[data-kop8-sheet-close]');
    if (close) close.focus();
  }

  function closeSheet() {
    var node = findSheet();
    if (!node || !node.hasAttribute('data-kop8-open')) return;
    node.removeAttribute('data-kop8-open');
    unlockScroll();
    if (lastTrigger && lastTrigger.isConnected) lastTrigger.focus();
    lastTrigger = null;
  }

  function pointsFrom(el, attr) {
    var raw = el.getAttribute(attr || 'data-kop8-points') || '';
    return raw
      .split('|')
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean);
  }

  /* Delegated from the document so cards injected by pagination, filtering,
     quick view or a section reload are covered without a remount. */
  doc.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    if (target.closest('[data-kop8-sheet-close]') || target.closest('[data-kop8-sheet-scrim]')) {
      event.preventDefault();
      closeSheet();
      return;
    }

    var trigger = target.closest('[data-kop8-info]');

    /* W77 acts on the card's OWN spec cells, which carry their data in every
       arm. The flag check lives here rather than in the markup, so the control
       card is byte identical to the theme's and simply does nothing on tap. */
    if (!trigger) {
      var spec = target.closest('[data-kop8-spec]');
      if (spec && (on('w77') || has('w8ed-card'))) trigger = spec;
    }

    if (!trigger) return;

    /* A chip lives inside the card's link. Without this the sheet opens and
       the browser navigates away from it in the same gesture. */
    event.preventDefault();
    event.stopPropagation();

    /* The copy arm swaps the visible line in CSS. The sheet has no CSS route
       to the same choice, so it reads the alternate set off the same trigger
       whenever W74b is the running arm. */
    var useAlt = on('w74b') && trigger.hasAttribute('data-kop8-title-alt');
    var title = trigger.getAttribute(useAlt ? 'data-kop8-title-alt' : 'data-kop8-title') || '';
    var points = pointsFrom(trigger, useAlt ? 'data-kop8-points-alt' : 'data-kop8-points');

    if (window.KopstormPop && typeof window.KopstormPop.open === 'function') {
      window.KopstormPop.open({ title: title, points: points, trigger: trigger });
      return;
    }
    openSheet(title, points, trigger);
  });

  doc.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeSheet();
  });

  /* ------------------------------------------------------------------------
     W75  Guarantee gallery slide promoted to position 2

     Edwin added the guarantee image as the LAST gallery image on every
     product, which is the one slide almost nobody reaches: Clarity shows the
     gallery arrows and the first three thumbnails taking nearly every click.
     K_16 already proved the gallery is where attention is, so this test moves
     the reassurance into it rather than adding another block below the fold.

     The media list and the thumbnail list are built in the same order, so
     both are moved together or the dots address the wrong slide.
     ------------------------------------------------------------------------ */

  var GALLERY_MOVED = 'data-kop8-moved';

  function promoteSlide(list, itemSelector) {
    if (!list || list.hasAttribute(GALLERY_MOVED)) return false;
    var items = list.querySelectorAll(':scope > ' + itemSelector);
    /* Below three slides there is no "position 2" worth having, and the move
       would just swap the last two. */
    if (items.length < 3) return false;

    var last = items[items.length - 1];
    var second = items[1];
    if (!last || !second || last === second) return false;

    /* Remember where it came from so the flag going off restores the theme's
       own order exactly. */
    last.setAttribute('data-kop8-origin', String(items.length - 1));
    list.insertBefore(last, second);
    list.setAttribute(GALLERY_MOVED, '');
    return true;
  }

  function restoreSlide(list, itemSelector) {
    if (!list || !list.hasAttribute(GALLERY_MOVED)) return false;
    var moved = list.querySelector(':scope > [data-kop8-origin]');
    if (moved) {
      moved.removeAttribute('data-kop8-origin');
      list.appendChild(moved);
    }
    list.removeAttribute(GALLERY_MOVED);
    return true;
  }

  /* Concept's slider-element caches its slide list in `_items` the first time
     it is read, and exposes reset() to re-read it. Reordering the DOM without
     that call leaves the arrows, the dots and the swipe target pointing at the
     positions the slides USED to hold, which looks like the gallery being
     broken rather than the test being on. Both the gallery slider and the
     thumbnail list are refreshed, and the thumbnails also address the gallery
     through aria-controls. */
  function refreshSlider(list) {
    var slider = list.closest && list.closest('slider-element');
    if (slider && typeof slider.reset === 'function') slider.reset();

    var controls = list.closest && list.closest('[aria-controls]');
    var targetId = controls && controls.getAttribute('aria-controls');
    var target = targetId ? doc.getElementById(targetId) : null;
    if (target && typeof target.reset === 'function') target.reset();

    var dots = list.querySelector && list.querySelector('[aria-controls]');
    if (dots) {
      var dotsTarget = doc.getElementById(dots.getAttribute('aria-controls'));
      if (dotsTarget && typeof dotsTarget.reset === 'function') dotsTarget.reset();
    }
  }

  function syncGallery() {
    var media = doc.querySelectorAll('.product__media-list');
    var thumbs = doc.querySelectorAll('.product__thumbnails-list');
    var apply = onGallery() ? promoteSlide : restoreSlide;

    media.forEach(function (list) {
      if (apply(list, '.product__media')) refreshSlider(list);
    });
    thumbs.forEach(function (list) {
      if (apply(list, '.product__thumbnail')) refreshSlider(list);
    });
  }

  /* ------------------------------------------------------------------------
     W76  Cart drawer reassurance and gift threshold

     The threshold is stated as a thing the shopper receives, never as a
     percentage. K_07 (-12.36% CR) and K_08 both showed that naming a discount
     on this store repels, while every reassurance test wins.
     ------------------------------------------------------------------------ */

  function cartTotalCents(scope) {
    var node = (scope || doc).querySelector('[data-kop8-cart-total]');
    if (node) {
      var value = parseInt(node.getAttribute('data-kop8-cart-total'), 10);
      if (!isNaN(value)) return value;
    }
    return null;
  }

  function paintCart(box, totalCents) {
    if (!box) return;
    var threshold = parseInt(box.getAttribute('data-kop8-threshold'), 10);
    if (isNaN(threshold) || threshold <= 0) return;

    var total = typeof totalCents === 'number' ? totalCents : cartTotalCents(doc);
    if (typeof total !== 'number') return;

    var met = total >= threshold;
    var pct = Math.max(0, Math.min(100, (total / threshold) * 100));

    var fill = box.querySelector('[data-kop8-fill]');
    if (fill) fill.style.width = pct + '%';

    var remaining = box.querySelector('[data-kop8-remaining]');
    if (remaining) {
      remaining.textContent = met
        ? t('threshold_met', 'Unlocked')
        : t('threshold_left', 'Add [amount]').replace('[amount]', money(threshold - total));
    }

    box.classList.toggle('kop8-cart--met', met);
  }

  function syncCart() {
    doc.querySelectorAll('[data-kop8-cart]').forEach(function (box) {
      paintCart(box);
    });
    syncUpCart();
  }

  /* UpCart renders in an OPEN DECLARATIVE SHADOW ROOT on #upCart. A clone
     placed in the host's light DOM is never displayed, and document CSS
     cannot reach inside, so the clone and its styles both go in. Wave 7 r5. */
  var UPCART_TAG = 'data-kop8-upcart';

  /* RE-ENTRY GUARD. The observer watches the shadow root subtree, and this
     function writes into that same subtree: inserting the clone and repainting
     the remaining amount both produce childList records, which call the
     observer, which calls this again. Without the guard the page locks up
     solid. Released on a macrotask so the records our own writes generated are
     consumed while the guard is still up. */
  var upcartBusy = false;

  function upcartHost() {
    var host = doc.getElementById('upCart');
    return host && host.shadowRoot ? host : null;
  }

  function syncUpCart() {
    if (upcartBusy) return;
    var host = upcartHost();
    if (!host) return;
    upcartBusy = true;
    try {
      syncUpCartInner(host);
    } finally {
      setTimeout(function () {
        upcartBusy = false;
      }, 0);
    }
  }

  function syncUpCartInner(host) {
    var shadow = host.shadowRoot;
    var existing = shadow.querySelector('[' + UPCART_TAG + ']');

    if (!onCart()) {
      if (existing) existing.remove();
      return;
    }

    var source = doc.querySelector('[data-kop8-cart]');
    if (!source) return;
    if (existing) {
      paintCart(existing);
      return;
    }

    if (!shadow.querySelector('[data-kop8-style]')) {
      var style = doc.createElement('style');
      style.setAttribute('data-kop8-style', '');
      /* Inside a shadow root the html flag class is invisible, so the clone
         is unconditionally visible and its presence is the switch. */
      style.textContent =
        '.kop8-cart{display:block;margin:0 0 12px;padding:12px;border:1px solid #ecdcd0;' +
        'border-radius:12px;background:#fdf7f3;color:#5a4632;font-size:14px}' +
        '.kop8-cart__head{display:flex;align-items:baseline;justify-content:space-between;gap:8px;margin:0 0 8px}' +
        '.kop8-cart__title{color:#3d2c1a}.kop8-cart__remaining{color:#6e4423;white-space:nowrap}' +
        '.kop8-cart__track{position:relative;height:6px;border-radius:999px;background:#ecdcd0;overflow:hidden}' +
        '.kop8-cart__fill{display:block;height:100%;width:0;border-radius:999px;background:#6e4423;transition:width .4s ease}' +
        '.kop8-cart--met .kop8-cart__fill{background:#2f6b46}' +
        '.kop8-cart__points{display:flex;flex-direction:column;gap:5px;margin:10px 0 0;padding:0;list-style:none}' +
        '.kop8-cart__point{display:flex;align-items:center;gap:7px}' +
        '.kop8-cart__point svg{flex:0 0 auto;width:14px;height:14px;color:#2f6b46}';
      shadow.appendChild(style);
    }

    var clone = source.cloneNode(true);
    clone.setAttribute(UPCART_TAG, '');
    clone.removeAttribute('id');

    /* Match the button or anchor itself. A '[class*=checkout]' selector also
       matches UpCart's own footer wrapper and sends the insert out of bounds. */
    var checkout =
      shadow.querySelector('button[class*="checkoutButton"]') ||
      shadow.querySelector('a[class*="checkoutButton"]') ||
      shadow.querySelector('button[class*="Checkout"]');
    var anchor = checkout ? checkout.closest('div') : null;

    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(clone, anchor);
    else shadow.appendChild(clone);

    paintCart(clone);
  }

  /* React re-renders wipe the clone, so the shadow root itself is observed. */
  var upcartObserver = null;

  function watchUpCart() {
    var host = upcartHost();
    if (!host || upcartObserver) return;
    upcartObserver = new MutationObserver(function () {
      syncUpCart();
    });
    upcartObserver.observe(host.shadowRoot, { childList: true, subtree: true });
    syncUpCart();
  }

  /* ------------------------------------------------------------------------
     Boot and resync
     ------------------------------------------------------------------------ */

  function syncAll() {
    syncGallery();
    syncCart();
  }

  function boot() {
    syncAll();
    watchUpCart();
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* An Intelligems arm switch, a ?kop= parameter and the editor preview all
     arrive through the same event. */
  doc.addEventListener('kopstorm:flag', function (event) {
    var id = event.detail && event.detail.id;
    if (!id) {
      syncAll();
      return;
    }
    if (id === 'w75' || id === 'w8preview') syncGallery();
    if (id === 'w76' || id === 'w8preview') syncCart();
  });

  /* Section reloads in the editor, cart refreshes, and quick view fetches. */
  doc.addEventListener('shopify:section:load', syncAll);
  doc.addEventListener('cart:refresh', syncCart);
  doc.addEventListener('cart:change', syncCart);
  doc.addEventListener('variant:change', syncGallery);

  /* UpCart mounts late, after its own bundle has run. */
  var lateBoot = 0;
  var lateTimer = setInterval(function () {
    lateBoot += 1;
    watchUpCart();
    if (upcartObserver || lateBoot > 20) clearInterval(lateTimer);
  }, 500);

  window.KopstormW8 = Object.assign(window.KopstormW8 || {}, {
    sync: syncAll,
    openSheet: openSheet,
    closeSheet: closeSheet
  });
})();
